import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LoaderCircle, X } from 'lucide-react';
import { Button } from '../Button';
import styles from './Modal.module.scss';

export type ModalSize = 'sm' | 'md' | 'lg';

type ModalProps = {
    isOpen: boolean;
    onClose: () => void;
    size?: ModalSize;
    title?: React.ReactNode;
    header?: React.ReactNode;
    children: React.ReactNode;
    footer?: React.ReactNode;
    showDefaultFooter?: boolean;
    cancelText?: string;
    doneText?: string;
    onCancel?: () => void;
    onDone?: () => void;
    closeOnOverlayClick?: boolean;
    closeOnEscape?: boolean;
    showCloseButton?: boolean;
    className?: string;
    overlayClassName?: string;
    contentClassName?: string;
    headerClassName?: string;
    footerClassName?: string;
    isLoadingOverlay?: boolean;
};

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    size = 'md',
    title,
    header,
    children,
    footer,
    showDefaultFooter = true,
    cancelText = 'Cancel',
    doneText = 'Done',
    onCancel,
    onDone,
    closeOnOverlayClick = true,
    closeOnEscape = true,
    showCloseButton = true,
    className = '',
    overlayClassName = '',
    contentClassName = '',
    headerClassName = '',
    footerClassName = '',
    isLoadingOverlay = false,
}) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const previousActiveElement = useRef<HTMLElement | null>(null);

    // Handle escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (closeOnEscape && event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [isOpen, closeOnEscape, onClose]);

    // Handle focus management
    useEffect(() => {
        if (isOpen) {
            // Store the currently focused element
            previousActiveElement.current = document.activeElement as HTMLElement;

            // Focus the modal
            if (modalRef.current) {
                modalRef.current.focus();
            }

            // Prevent body scrolling
            document.body.style.overflow = 'hidden';
        } else {
            // Restore focus to the previously focused element
            if (previousActiveElement.current) {
                previousActiveElement.current.focus();
            }

            // Restore body scrolling
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // Handle overlay click
    const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (closeOnOverlayClick && event.target === event.currentTarget) {
            onClose();
        }
    };

    // Handle default footer actions
    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        } else {
            onClose();
        }
    };

    const handleDone = () => {
        if (onDone) {
            onDone();
        } else {
            onClose();
        }
    };

    // Render header
    const renderHeader = () => {
        if (header) {
            return <div className={`${styles.header} ${headerClassName}`}>{header}</div>;
        }

        if (title || showCloseButton) {
            return (
                <div className={`${styles.header} ${headerClassName}`}>
                    {title && (
                        <div className={styles.title}>{typeof title === 'string' ? <h2>{title}</h2> : title}</div>
                    )}
                    {showCloseButton && (
                        <Button
                            className={styles.closeButton}
                            variant='ghost'
                            onClick={onClose}
                            aria-label='Close modal'
                            iconOnly
                            isRounded
                            icon={X}
                        />
                    )}
                </div>
            );
        }

        return null;
    };

    // Render footer
    const renderFooter = () => {
        if (footer !== undefined) {
            return footer && <div className={`${styles.footer} ${footerClassName}`}>{footer}</div>;
        }

        if (showDefaultFooter) {
            return (
                <div className={`${styles.footer} ${footerClassName}`}>
                    <div className={styles.footerActions}>
                        <Button variant='outline-secondary' onClick={handleCancel}>
                            {cancelText}
                        </Button>
                        <Button variant='primary' onClick={handleDone} disabled={isLoadingOverlay}>
                            {doneText}
                        </Button>
                    </div>
                </div>
            );
        }

        return null;
    };

    // Don't render if not open
    if (!isOpen) return null;

    const modalContent = (
        <div
            className={`${styles.overlay} ${overlayClassName}`}
            onClick={handleOverlayClick}
            role='dialog'
            aria-modal='true'
            aria-labelledby={title ? 'modal-title' : undefined}
        >
            <div ref={modalRef} className={`${styles.modal} ${styles[size]} ${className}`} tabIndex={-1}>
                <div className={`${styles.content} ${contentClassName}`}>
                    {renderHeader()}

                    <div className={styles.body}>
                        {children}
                        {isLoadingOverlay && (
                            <div className={styles.loadingOverlay}>
                                <LoaderCircle size={26} className={styles.spinner} color='var(--primary-purple)' />{' '}
                                Loading...
                            </div>
                        )}
                    </div>

                    {renderFooter()}
                </div>
            </div>
        </div>
    );

    // Render in portal
    return createPortal(modalContent, document.body);
};
